import React from "react";
import type { Recipe as RecipeT } from "./types";
import Icon from "./Icon";
import RecipePlaceholder from "./RecipePlaceholder";

interface RecipeProps {
  recipe: RecipeT;
  onBack: () => void;
}

type WakeLockSentinel = { release: () => Promise<void> };

const HOLD_TO_JUMP_MS = 600;
const POINTER_MOVE_TOLERANCE = 12;
const PLAY_GUIDE_STORAGE_KEY = "recipe-portfolio:play-guide-until:v1";
const PLAY_GUIDE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const shouldShowPlayGuide = () => {
  try {
    const storedUntil = window.localStorage.getItem(PLAY_GUIDE_STORAGE_KEY);
    if (storedUntil === null) return true;
    const until = Number(storedUntil);
    return !Number.isFinite(until) || until <= Date.now();
  } catch {
    return true;
  }
};

const fmtQty = (q: number): string => {
  if (q === 0) return "";
  if (Number.isInteger(q)) return String(q);
  const frac: Record<string, string> = {
    "0.25": "¼", "0.5": "½", "0.75": "¾", "0.33": "⅓", "0.67": "⅔",
  };
  const whole = Math.floor(q);
  const rest = +(q - whole).toFixed(2);
  const f = frac[rest.toString()];
  if (whole === 0 && f) return f;
  if (f) return `${whole}${f}`;
  return q.toString();
};

interface SourceLabel {
  text: string;
  link: string | null;
  deleted: boolean;
  platform: string;
}

const deriveSource = (recipe: RecipeT): SourceLabel => {
  let host = "";
  let platform = "source";
  let autoLabel = "source";
  try {
    const u = new URL(recipe.source);
    host = u.hostname.replace(/^www\./, "");
    if (host.includes("instagram.com")) {
      platform = "instagram";
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "p") autoLabel = "@instagram";
      else if (parts[0]) autoLabel = "@" + parts[0].replace(/^@/, "") + " on instagram";
      else autoLabel = "instagram";
    } else if (host.includes("youtube.com") || host.includes("youtu.be")) {
      platform = "youtube";
      autoLabel = "youtube";
    } else if (host.includes("tiktok.com")) {
      platform = "tiktok";
      const parts = u.pathname.split("/").filter(Boolean);
      autoLabel = parts[0] ? `${parts[0]} on tiktok` : "tiktok";
    } else {
      platform = host;
      autoLabel = host;
    }
  } catch {
    // keep fallbacks
  }

  const deleted = recipe.deleted === true;
  const creator = recipe.creator && recipe.creator.trim() ? recipe.creator.trim() : null;

  if (deleted) {
    const base = creator ? `${creator} on ${platform}` : platform;
    return { text: `${base} (deleted)`, link: null, deleted: true, platform };
  }
  if (creator) {
    return { text: `${creator} on ${platform}`, link: recipe.source, deleted: false, platform };
  }
  return { text: autoLabel, link: recipe.source, deleted: false, platform };
};

const Recipe: React.FC<RecipeProps> = ({ recipe, onBack }) => {
  const [servings, setServings] = React.useState(recipe.servings);
  const [hoveredStep, setHoveredStep] = React.useState<number | null>(null);
  const [playMode, setPlayMode] = React.useState(false);
  const [playStep, setPlayStep] = React.useState(1);
  const [holdingStep, setHoldingStep] = React.useState<number | null>(null);
  const [showPlayGuide, setShowPlayGuide] = React.useState(false);
  const wakeLock = React.useRef<WakeLockSentinel | null>(null);
  const playGuideButton = React.useRef<HTMLButtonElement | null>(null);
  const playSurfaceGesture = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
    holdFired: boolean;
    holdTimer: number | null;
  } | null>(null);

  const total = recipe.methods.length;
  const activeStep = playMode ? playStep : hoveredStep;

  const multiplier = servings / recipe.servings;
  const scaledIngredients = recipe.ingredients.map((i) => ({
    ...i,
    quantity: +(i.quantity * multiplier).toFixed(2),
  }));

  const source = React.useMemo(() => deriveSource(recipe), [recipe]);

  const next = React.useCallback(
    () => setPlayStep((s) => Math.min(total, s + 1)),
    [total]
  );
  const prev = React.useCallback(
    () => setPlayStep((s) => Math.max(1, s - 1)),
    []
  );

  const dismissPlayGuide = React.useCallback(() => {
    try {
      window.localStorage.setItem(
        PLAY_GUIDE_STORAGE_KEY,
        String(Date.now() + PLAY_GUIDE_TTL_MS)
      );
    } catch {
      /* The guide still closes when storage is unavailable. */
    }
    setShowPlayGuide(false);
  }, []);

  React.useEffect(() => {
    if (showPlayGuide) playGuideButton.current?.focus();
  }, [showPlayGuide]);

  React.useEffect(() => {
    if (!playMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (showPlayGuide) return;
      if (isInteractiveTarget(e.target)) return;
      if (e.code === "Space" || e.code === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.code === "Escape") {
        setPlayMode(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playMode, showPlayGuide, next, prev]);

  const isInteractiveTarget = (target: EventTarget | null) =>
    target instanceof Element &&
    !!target.closest("button, a, input, select, textarea, [role='button']");

  const onPlaySurfacePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (!playMode || showPlayGuide || !e.isPrimary) return;
    const target = e.target instanceof Element ? e.target : null;
    const stepElement = target?.closest<HTMLElement>("[data-step]") ?? null;
    if (!stepElement && isInteractiveTarget(target)) return;

    const gesture = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      holdFired: false,
      holdTimer: null as number | null,
    };
    const step = stepElement ? Number(stepElement.dataset.step) : null;
    if (step) {
      setHoldingStep(step);
      gesture.holdTimer = window.setTimeout(() => {
        const current = playSurfaceGesture.current;
        if (!current || current.pointerId !== e.pointerId || current.moved) return;
        current.holdFired = true;
        current.holdTimer = null;
        setHoldingStep(null);
        setPlayStep(step);
        navigator.vibrate?.(10);
      }, HOLD_TO_JUMP_MS);
    }
    playSurfaceGesture.current = gesture;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPlaySurfacePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const gesture = playSurfaceGesture.current;
    if (!gesture || gesture.pointerId !== e.pointerId) return;
    if (
      Math.hypot(e.clientX - gesture.startX, e.clientY - gesture.startY) >
      POINTER_MOVE_TOLERANCE
    ) {
      gesture.moved = true;
      if (gesture.holdTimer !== null) window.clearTimeout(gesture.holdTimer);
      gesture.holdTimer = null;
      setHoldingStep(null);
    }
  };

  const onPlaySurfacePointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const gesture = playSurfaceGesture.current;
    playSurfaceGesture.current = null;
    if (!gesture || gesture.pointerId !== e.pointerId) return;
    if (gesture.holdTimer !== null) window.clearTimeout(gesture.holdTimer);
    setHoldingStep(null);
    if (gesture.moved || gesture.holdFired) return;
    if (e.clientX < window.innerWidth / 2) prev();
    else next();
  };

  const onPlaySurfacePointerCancel = () => {
    const gesture = playSurfaceGesture.current;
    if (gesture?.holdTimer != null) window.clearTimeout(gesture.holdTimer);
    playSurfaceGesture.current = null;
    setHoldingStep(null);
  };

  React.useEffect(
    () => () => {
      const gesture = playSurfaceGesture.current;
      if (gesture?.holdTimer != null) window.clearTimeout(gesture.holdTimer);
    },
    []
  );

  React.useEffect(() => {
    if (!playMode) return;
    (async () => {
      try {
        const nav = navigator as Navigator & {
          wakeLock?: { request: (t: "screen") => Promise<WakeLockSentinel> };
        };
        if (nav.wakeLock) {
          wakeLock.current = await nav.wakeLock.request("screen");
        }
      } catch {
        /* noop */
      }
    })();
    return () => {
      if (wakeLock.current) {
        try { wakeLock.current.release(); } catch { /* noop */ }
        wakeLock.current = null;
      }
    };
  }, [playMode]);

  const togglePlay = () => {
    setPlayMode((p) => {
      if (!p) {
        setPlayStep(1);
        setShowPlayGuide(shouldShowPlayGuide());
      } else {
        setShowPlayGuide(false);
      }
      return !p;
    });
  };

  React.useEffect(() => {
    if (!playMode) return;
    const el = document.querySelector<HTMLElement>(`[data-step="${playStep}"]`);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (el) el.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
  }, [playMode, playStep]);

  return (
    <main
      className="rp-main"
      data-mobile-focus={playMode ? "true" : "false"}
      style={{ maxWidth: 1240, margin: "0 auto", padding: "24px 24px 64px", width: "100%" }}
    >
      <button
        onClick={onBack}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "var(--mono)",
          fontSize: 12,
          color: "var(--fg-2)",
          marginBottom: 20,
        }}
      >
        <Icon name="arrow-left" size={13} /> all recipes
      </button>

      <header
        className="rp-recipe-header"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 220px",
          gap: 28,
          alignItems: "start",
          marginBottom: 24,
          paddingBottom: 20,
          borderBottom: "1px dashed var(--rule)",
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "var(--mono)",
              fontWeight: 700,
              fontSize: "clamp(28px, 4vw, 44px)",
              lineHeight: 1.1,
              margin: 0,
              marginBottom: 10,
              letterSpacing: "-0.01em",
            }}
          >
            {recipe.name.toLowerCase()}
          </h1>
          <div
            style={{
              fontFamily: "var(--mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              marginBottom: 14,
            }}
          >
            modified from{" "}
            {source.link ? (
              <a href={source.link} target="_blank" rel="noreferrer" className="link-underline">
                {source.text} <Icon name="external" size={10} />
              </a>
            ) : (
              <span style={{ color: "var(--fg-3)" }}>{source.text}</span>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--fg-2)" }}>
              servings
            </span>
            <button onClick={() => setServings((s) => Math.max(1, s - 1))} style={stepperBtn}>
              −
            </button>
            <div
              style={{
                fontFamily: "var(--mono)",
                fontSize: 20,
                fontWeight: 700,
                minWidth: 28,
                textAlign: "center",
              }}
            >
              {servings}
            </div>
            <button onClick={() => setServings((s) => s + 1)} style={stepperBtn}>
              +
            </button>
            <div style={{ display: "flex", gap: 4, marginLeft: 6 }}>
              {[1, 2, 3, 4].map((m) => {
                const val = recipe.servings * m;
                const active = val === servings;
                return (
                  <button
                    key={m}
                    onClick={() => setServings(val)}
                    style={{
                      padding: "3px 9px",
                      fontFamily: "var(--mono)",
                      fontSize: 11,
                      color: active ? "var(--bg)" : "var(--fg-2)",
                      background: active ? "var(--fg)" : "transparent",
                      border: `1px solid ${active ? "var(--fg)" : "var(--rule)"}`,
                      borderRadius: 2,
                    }}
                  >
                    {m}x
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className="rp-recipe-header-photo"
          style={{
            aspectRatio: "1/1",
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid var(--rule-soft)",
          }}
        >
          <RecipePlaceholder recipe={recipe} />
        </div>
      </header>

      <section
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <button
          onClick={togglePlay}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 14px",
            background: playMode ? "var(--fg)" : "var(--accent)",
            color: "var(--bg)",
            border: `1px solid ${playMode ? "var(--fg)" : "var(--accent)"}`,
            borderRadius: 3,
            fontFamily: "var(--mono)",
            fontSize: 13,
            fontWeight: 700,
            minHeight: 44,
          }}
        >
          <Icon name={playMode ? "stop" : "play"} size={13} />
          {playMode ? "stop" : "cooking mode"}
        </button>

        {playMode && (
          <>
            <button onClick={prev} disabled={playStep === 1} style={playNavBtn(playStep === 1)}>
              <Icon name="arrow-left" size={12} /> prev
            </button>
            <button onClick={next} disabled={playStep === total} style={playNavBtn(playStep === total)}>
              next <Icon name="arrow-right" size={12} />
            </button>
            <div
              style={{
                fontFamily: "var(--mono)",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--fg-2)",
              }}
            >
              step {playStep} / {total}
            </div>
            <div
              className="rp-play-hint"
              style={{
                marginLeft: "auto",
                fontFamily: "var(--mono)",
                fontSize: 11,
                color: "var(--fg-3)",
              }}
            >
              tap left/right · hold a step to jump · keyboard ← →
            </div>
          </>
        )}
      </section>

      {playMode && (
        <div
          role="progressbar"
          aria-label="Recipe progress"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={playStep}
          style={{
            height: 2,
            background: "var(--rule-soft)",
            position: "relative",
            marginBottom: 18,
          }}
        >
          <div
            className="rp-progress-fill"
            style={{
              position: "absolute",
              inset: 0,
              width: `${(playStep / total) * 100}%`,
              background: "var(--accent)",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      )}

      <div className="rp-sr-only" aria-live="polite" aria-atomic="true">
        {playMode ? `Step ${playStep} of ${total}` : ""}
      </div>

      <section
        className="rp-recipe-split"
        data-cooking-mode={playMode ? "true" : "false"}
        onPointerDown={onPlaySurfacePointerDown}
        onPointerMove={onPlaySurfacePointerMove}
        onPointerUp={onPlaySurfacePointerUp}
        onPointerCancel={onPlaySurfacePointerCancel}
        onContextMenu={(e) => {
          if (playMode && e.target instanceof Element && e.target.closest("[data-step]")) {
            e.preventDefault();
          }
        }}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.2fr)",
          gap: 40,
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "var(--mono)",
              fontSize: 14,
              fontWeight: 700,
              marginTop: 0,
              marginBottom: 10,
              color: "var(--fg-2)",
            }}
          >
            <span style={{ color: "var(--accent)" }}># </span>ingredients
          </h2>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {scaledIngredients.map((ing, i) => {
              const linked = activeStep !== null && ing.steps.includes(activeStep);
              const dim = activeStep !== null && !linked;
              return (
                <li
                  key={i}
                  className="rp-ingredient"
                  data-linked={linked ? "true" : "false"}
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "6px 0",
                    fontFamily: "var(--mono)",
                    fontSize: 14,
                    opacity: dim ? 0.35 : 1,
                    color: linked ? "var(--accent)" : "var(--fg)",
                    fontWeight: linked ? 700 : 400,
                    transition: "opacity 0.15s, color 0.15s",
                  }}
                >
                  <span
                    style={{
                      minWidth: 56,
                      color: linked ? "var(--accent)" : "var(--fg-2)",
                    }}
                  >
                    {fmtQty(ing.quantity)}
                    {ing.unit ? ` ${ing.unit}` : ""}
                  </span>
                  <span>{ing.name}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h2
            style={{
              fontFamily: "var(--mono)",
              fontSize: 14,
              fontWeight: 700,
              marginTop: 0,
              marginBottom: 10,
              color: "var(--fg-2)",
              display: "flex",
              alignItems: "baseline",
              gap: 10,
            }}
          >
            <span>
              <span style={{ color: "var(--accent)" }}># </span>methods
            </span>
            {!playMode && (
              <span style={{ fontWeight: 400, color: "var(--fg-3)", fontSize: 11 }}>
                hover a step →
              </span>
            )}
          </h2>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {recipe.methods.map((step, i) => {
              const n = i + 1;
              const active = activeStep === n;
              const isNext = playMode && n === playStep + 1;
              const faded = playMode && !active;
              return (
                <li
                  key={i}
                  className="rp-method"
                  data-step={n}
                  data-active={active ? "true" : "false"}
                  data-next={isNext ? "true" : "false"}
                  data-holding={holdingStep === n ? "true" : "false"}
                  role={playMode ? "button" : undefined}
                  tabIndex={playMode ? 0 : undefined}
                  aria-current={active && playMode ? "step" : undefined}
                  aria-label={playMode ? `Step ${n} of ${total}. Press Enter to jump here.` : undefined}
                  onMouseEnter={() => !playMode && setHoveredStep(n)}
                  onMouseLeave={() => !playMode && setHoveredStep(null)}
                  onKeyDown={(e) => {
                    if (!playMode || (e.key !== "Enter" && e.key !== " ")) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setPlayStep(n);
                  }}
                  onClick={(e) => {
                    if (!playMode) return;
                    e.stopPropagation();
                    if (e.detail === 0) setPlayStep(n);
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "30px 1fr",
                    gap: 12,
                    padding: "10px 10px 10px 6px",
                    background: active ? "var(--paper-2)" : "transparent",
                    borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                    transition: "background 0.15s, border-color 0.15s, opacity 0.15s",
                    fontFamily: "var(--mono)",
                    fontSize: 14,
                    lineHeight: 1.5,
                    opacity: faded ? 0.35 : 1,
                    fontWeight: active && playMode ? 700 : 400,
                    cursor: playMode ? "pointer" : "default",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      color: active ? "var(--accent)" : "var(--fg-3)",
                      fontWeight: 700,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {n}.
                  </span>
                  <span style={{ position: "relative", zIndex: 1 }}>{step}</span>
                </li>
              );
            })}
          </ol>

          {recipe.notes && recipe.notes.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 14,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "var(--fg-2)",
                }}
              >
                <span style={{ color: "var(--accent)" }}># </span>notes
              </h3>
              <ol
                style={{
                  paddingLeft: 20,
                  color: "var(--fg-2)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                {recipe.notes.map((n, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {n}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </section>

      {showPlayGuide && (
        <div
          className="rp-play-guide"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rp-play-guide-title"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              dismissPlayGuide();
            } else if (e.key === "Tab") {
              e.preventDefault();
              playGuideButton.current?.focus();
            }
          }}
        >
          <div className="rp-play-guide-heading">
            <span>first time here?</span>
            <h2 id="rp-play-guide-title">cooking mode controls</h2>
          </div>

          <div className="rp-play-guide-zone rp-play-guide-zone-left">
            <Icon name="arrow-left" size={34} stroke={1.3} />
            <strong>tap left</strong>
            <span>previous step</span>
          </div>
          <div className="rp-play-guide-zone rp-play-guide-zone-right">
            <Icon name="arrow-right" size={34} stroke={1.3} />
            <strong>tap right</strong>
            <span>next step</span>
          </div>

          <div className="rp-play-guide-hold">
            <span className="rp-play-guide-hold-mark" aria-hidden="true" />
            <strong>hold a step</strong>
            <span>jump straight there</span>
          </div>

          <div className="rp-play-guide-footer">
            <button ref={playGuideButton} onClick={dismissPlayGuide}>
              start cooking
            </button>
            <span>we'll show this reminder again in a week</span>
          </div>
        </div>
      )}
    </main>
  );
};

const stepperBtn: React.CSSProperties = {
  width: 44,
  height: 44,
  border: "1px solid var(--rule)",
  fontFamily: "var(--mono)",
  fontSize: 14,
  color: "var(--fg-2)",
  borderRadius: 2,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const playNavBtn = (disabled: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 12px",
  border: "1px solid var(--rule)",
  background: "transparent",
  color: "var(--fg-2)",
  fontFamily: "var(--mono)",
  fontSize: 12,
  borderRadius: 3,
  minHeight: 44,
  opacity: disabled ? 0.4 : 1,
});

export default Recipe;
